import { animate } from '@play-co/timestep-core/ui';
import { randomItem } from '../../../odie';
import type { IEffect } from '../../actions/effects/IEffect';
import { app } from '../../app';
import {
  despawnOverlayEntity,
  spawnOverlayEntity,
} from '../../entities/OverlayEntity';
import type { GameScene } from '../../GameScene';
import { getMatch2AssetPath } from '../../util/assets';
import type { GrassBlockEntity } from './GrassBlockEntity';

const leafTextures = [
  getMatch2AssetPath('blocks/grass/leaf_1.png'),
  getMatch2AssetPath('blocks/grass/leaf_2.png'),
  getMatch2AssetPath('blocks/grass/leaf_3.png'),
];

const mushroomTextures = [
  getMatch2AssetPath('blocks/grass/mushroom_1.png'),
  getMatch2AssetPath('blocks/grass/mushroom_2.png'),
  getMatch2AssetPath('blocks/grass/mushroom_3.png'),
];

const grassHitSounds = [
  'puzzle-grass-01',
  'puzzle-grass-02',
  'puzzle-grass-03',
];

export class GrassBlockEffect implements IEffect {
  private readonly scene: GameScene;
  private readonly entity: GrassBlockEntity;

  constructor(scene: GameScene, entity: GrassBlockEntity) {
    this.scene = scene;
    this.entity = entity;
  }

  public async execute() {
    //
  }

  public async playHitParticles(hasMushrooms: boolean) {
    app.sound.play(randomItem(grassHitSounds));

    const promises = [this.playLeafParticles()];
    if (hasMushrooms) {
      promises.push(this.playMushroomParticles());
    }

    await Promise.all(promises);
  }

  private async playLeafParticles() {
    const particles = app.particles.create({
      textures: leafTextures,
      rate: 0,
      limit: 14,
      emitterDuration: 0,
      duration: 1,
      velocity: [
        { x: -120, y: -600 },
        { x: 120, y: -700 },
      ],
      scale: [
        { x: 0.55, y: 0.55 },
        { x: 0.9, y: 0.9 },
      ],
      behaviors: [
        { type: 'explode', magnitude: [280, 430], applyRotation: true },
        { type: 'gravity', gravity: { x: 0, y: 2000 } },
        { type: 'fade', to: 0, tween: animate.easeInExpo },
        { type: 'kinematic', directionalRotation: true },
      ],
    });

    const overlay = spawnOverlayEntity(
      this.scene,
      this.entity.c.position.mapPosition.clone(),
      this.entity.c.block.props,
      () => particles.view,
      'foreground',
    );
    await particles.start();
    despawnOverlayEntity(this.scene, overlay);
  }

  private async playMushroomParticles() {
    const particles = app.particles.create({
      textures: mushroomTextures,
      rate: 0,
      limit: 5,
      emitterDuration: 0,
      duration: 1,
      velocity: [
        { x: -70, y: -460 },
        { x: 70, y: -620 },
      ],
      scale: [
        { x: 0.7, y: 0.7 },
        { x: 1.0, y: 1.0 },
      ],
      behaviors: [
        { type: 'explode', magnitude: [180, 340], applyRotation: true },
        { type: 'gravity', gravity: { x: 0, y: 3000 } },
        { type: 'scale', to: 0 },
        { type: 'kinematic', directionalRotation: true },
      ],
    });

    const overlay = spawnOverlayEntity(
      this.scene,
      this.entity.c.position.mapPosition.clone(),
      this.entity.c.block.props,
      () => particles.view,
      'foreground',
    );
    await particles.start();
    despawnOverlayEntity(this.scene, overlay);
  }
}
